import ky, { type SearchParamsOption } from 'ky';
import { env } from '~/lib/env/client';
import { createLogger } from '../../lib/logging/createLogger';
import { handleErrorMessage } from './handleErrorMessage';
import type { GetError, PostError } from './types';

const logger = createLogger('api');
export const get = async <T extends object>(
  path: string,
  token: string,
  params?: SearchParamsOption
) => {
  try {
    logger.debug({ path, params }, 'Buscando dados...');
    const response = await ky
      .get<T | GetError>(`${env.VITE_API_ENTRYPOINT}/${path}`, {
        searchParams: params,
        headers: {
          token: env.VITE_TOKEN,
          Authorization: `Token ${token}`,
        },
      })
      .json();
    // API retorna 200 mesmo quando não há dados, ex: https://musea-staging.herokuapp.com/exhibitions/api/exhibitions/idquenaoexiste
    if ('detail' in response) {
      throw new Error(response.detail);
    }
    return response;
  } catch {
    throw new Error('Não foi possível carregar os dados');
  }
};

export const post = async <T extends object>(path: string, body?: object, token?: string) => {
  const url = `${env.VITE_API_ENTRYPOINT}/${path}`;
  const options = {
    json: body,
    headers: {
      token: env.VITE_TOKEN,
      ...(token ? { Authorization: `Token ${token}` } : {}),
    },
  };
  logger.debug({ url, body }, 'Enviando dados...');
  const result = await ky.post(url, options);
  const response = await result.json();
  logger.debug({ response, ok: result.ok }, 'Resposta recebida');
  if (result.ok) {
    logger.debug('Operação bem sucedida');
    return response as T;
  }
  const error = handleErrorMessage(response as PostError);
  throw new Error(error ?? 'Não foi possível executar a operação');
};

export const del = async <T extends object>(path: string, token: string, body: object = {}) => {
  const url = `${env.VITE_API_ENTRYPOINT}/${path}`;
  const options = {
    json: body,
    headers: {
      token: env.VITE_TOKEN,
      Authorization: `Token ${token}`,
    },
  };
  logger.info({ url }, 'Removendo dados...');
  const result = await ky.delete(url, options);
  if (result.status === 204) {
    return { success: true, detail: null };
  }
  try {
    logger.info({ ok: result.ok }, 'Resposta recebida');
    const response = await result.json();
    if (result.ok) {
      logger.info('Operação bem sucedida');
      return response as T;
    }
    const error = handleErrorMessage(response as PostError);
    if (error) throw new Error(error);
  } catch (error) {
    logger.error(error);
  }
  throw new Error('Não foi possível executar a operação');
  // const response = await tryCatch<PostResult, []>(result.json)();
};
